import React, { useCallback, useState } from "react";
import { toast } from "react-toastify";
import apiClient from "../../api/client";
import { getErrorMessage } from "../../utils/network";
import ProviderBranchPicker from "../ProviderBranchPicker";

const fromAccount = (a) => ({
  id: a.id,
  username: a.username || "",
  password: "",
  networkName: a.networkName || "",
  operatorName: a.operatorName || "",
  phoneNumber: a.phoneNumber || "",
  Email: a.Email || "",
  address: a.address || "",
  packPrice: a.packPrice,
  operCode: a.operCode,
  watchoOperCode: a.watchoOperCode || "",
  role: a.role,
  created_by: a.created_by,
  operatorEnabled: Number(a.operatorEnabled) === 1 ? 1 : 0,
  internetEnabled: Number(a.internetEnabled) === 1 ? 1 : 0,
  internetBaseUrl: a.internetBaseUrl || "",
  internetToken: a.internetToken || "",
  internetPartnerId: a.internetPartnerId ? String(a.internetPartnerId) : "",
  internetBranchId: a.internetBranchId ? String(a.internetBranchId) : "",
  railtelEnabled: Number(a.railtelEnabled) === 1 ? 1 : 0,
  dvplEnabled: Number(a.dvplEnabled) === 1 ? 1 : 0,
  dvplBaseUrl: a.dvplBaseUrl || "",
  dvplToken: a.dvplToken || "",
  dvplPartnerId: a.dvplPartnerId ? String(a.dvplPartnerId) : "",
  dvplBranchId: a.dvplBranchId ? String(a.dvplBranchId) : "",
  pioneeriptvOprId: a.pioneeriptvOprId || "",
  pioneeriptvToken: a.pioneeriptvToken || "",
  // Internet / DVPL recharges need an Aadhaar verified customer (default: yes)
  aadhaarRequired: a.aadhaarRequired === undefined || a.aadhaarRequired === null || Number(a.aadhaarRequired) === 1 ? 1 : 0,
});

const Toggle = ({ label, checked, onChange }) => (
  <label className="acc-toggle">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked ? 1 : 0)} />
    <span className="acc-toggle__track" aria-hidden="true" />
    <span>{label}</span>
  </label>
);

/** Edit an admin / operator. Provider settings are shown to the super admin only. */
const AccountEditModal = ({ account, isSuperAdmin, onClose, onSaved }) => {
  const [form, setForm] = useState(() => fromAccount(account));
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const patch = (p) => setForm((f) => ({ ...f, ...p }));
  const onInternetPick = useCallback(({ partnerId, branchId }) => setForm((f) => ({ ...f, internetPartnerId: partnerId || "", internetBranchId: branchId || "" })), []);
  const onDvplPick = useCallback(({ partnerId, branchId }) => setForm((f) => ({ ...f, dvplPartnerId: partnerId || "", dvplBranchId: branchId || "" })), []);

  const save = async () => {
    if (!form.username.trim() || !form.operatorName.trim() || !form.phoneNumber.trim() || !form.networkName.trim() || !form.Email.trim() || !form.address.trim()) {
      toast.warn("Fill in username, network name, name, phone, email and address.");
      return;
    }
    setSaving(true);
    try {
      await apiClient.put("/operator_api.php", form);
      toast.success("Account updated");
      onSaved && onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update the account"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="acc-overlay" role="dialog" aria-modal="true" aria-label="Edit account">
      <div className="acc-modal">
        <div className="acc-modal__head">
          <h3>Edit {form.operatorName || form.username}</h3>
          <button type="button" className="acc-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="acc-modal__body">
          <section className="acc-sec">
            <h4>Profile</h4>
            <div className="acc-grid">
              <label className="acc-f"><span>Username</span><input value={form.username} onChange={set("username")} /></label>
              <label className="acc-f">
                <span>Password</span>
                <div className="acc-pw">
                  <input type={showPw ? "text" : "password"} value={form.password} onChange={set("password")}
                    placeholder="Leave blank to keep" autoComplete="new-password" />
                  <button type="button" onClick={() => setShowPw((v) => !v)}>{showPw ? "Hide" : "Show"}</button>
                </div>
              </label>
              <label className="acc-f"><span>Network name</span><input value={form.networkName} onChange={set("networkName")} /></label>
              <label className="acc-f"><span>Name</span><input value={form.operatorName} onChange={set("operatorName")} /></label>
              <label className="acc-f"><span>Phone</span><input value={form.phoneNumber} onChange={set("phoneNumber")} /></label>
              <label className="acc-f"><span>Email</span><input value={form.Email} onChange={set("Email")} /></label>
              <label className="acc-f acc-span2"><span>Address</span><input value={form.address} onChange={set("address")} /></label>
            </div>
          </section>

          <section className="acc-sec">
            <h4>Recharge rules</h4>
            <Toggle label="Aadhaar verification required for Internet / DVPL recharges"
              checked={form.aadhaarRequired === 1} onChange={(v) => patch({ aadhaarRequired: v })} />
            <p className="acc-hint">
              {form.aadhaarRequired === 1
                ? "Customers must be Aadhaar verified before they can be recharged."
                : "Customers can be recharged without Aadhaar verification (customer verification is still needed)."}
            </p>
          </section>

          {isSuperAdmin && (
            <>
              <section className="acc-sec">
                <h4>Account</h4>
                <div className="acc-grid">
                  <label className="acc-f">
                    <span>Role</span>
                    <select value={form.role} onChange={set("role")}>
                      <option value="operator">Operator</option>
                      <option value="admin">Admin</option>
                    </select>
                  </label>
                  <label className="acc-f"><span>Watcho operator code</span><input value={form.watchoOperCode} onChange={set("watchoOperCode")} /></label>
                </div>
                <Toggle label="Account enabled" checked={form.operatorEnabled === 1} onChange={(v) => patch({ operatorEnabled: v })} />
              </section>

              <section className="acc-sec">
                <h4>Internet (Pioneer broadband)</h4>
                <Toggle label="Internet enabled" checked={form.internetEnabled === 1} onChange={(v) => patch({ internetEnabled: v })} />
                {form.internetEnabled === 1 && (
                  <>
                    <div className="acc-grid">
                      <label className="acc-f"><span>Base URL</span><input value={form.internetBaseUrl} onChange={set("internetBaseUrl")} placeholder="https://radius.example.com" /></label>
                      <label className="acc-f"><span>Token</span><input type="password" value={form.internetToken} onChange={set("internetToken")} placeholder="Bearer token" autoComplete="new-password" /></label>
                    </div>
                    <ProviderBranchPicker
                      type="internet"
                      baseUrl={form.internetBaseUrl}
                      token={form.internetToken}
                      accountId={form.id}
                      partnerId={form.internetPartnerId}
                      branchId={form.internetBranchId}
                      onChange={onInternetPick}
                      branchName={form.username}
                      address={form.address}
                    />
                  </>
                )}
              </section>

              <section className="acc-sec">
                <h4>DVPL</h4>
                <Toggle label="DVPL enabled" checked={form.dvplEnabled === 1} onChange={(v) => patch({ dvplEnabled: v })} />
                {form.dvplEnabled === 1 && (
                  <>
                    <div className="acc-grid">
                      <label className="acc-f"><span>Base URL</span><input value={form.dvplBaseUrl} onChange={set("dvplBaseUrl")} placeholder="https://radius.example.com" /></label>
                      <label className="acc-f"><span>Token</span><input type="password" value={form.dvplToken} onChange={set("dvplToken")} placeholder="Bearer token" autoComplete="new-password" /></label>
                    </div>
                    <ProviderBranchPicker
                      type="dvpl"
                      baseUrl={form.dvplBaseUrl}
                      token={form.dvplToken}
                      accountId={form.id}
                      partnerId={form.dvplPartnerId}
                      branchId={form.dvplBranchId}
                      onChange={onDvplPick}
                      branchName={form.username}
                      address={form.address}
                    />
                  </>
                )}
              </section>

              <section className="acc-sec">
                <h4>RailTel &amp; Pioneer IPTV</h4>
                <Toggle label="RailTel enabled" checked={form.railtelEnabled === 1} onChange={(v) => patch({ railtelEnabled: v })} />
                <div className="acc-grid">
                  <label className="acc-f"><span>Pioneer IPTV operator ID</span><input value={form.pioneeriptvOprId} onChange={set("pioneeriptvOprId")} /></label>
                  <label className="acc-f"><span>Pioneer IPTV token</span><input type="password" value={form.pioneeriptvToken} onChange={set("pioneeriptvToken")} autoComplete="new-password" /></label>
                </div>
              </section>
            </>
          )}
        </div>

        <div className="acc-modal__foot">
          <button type="button" className="acc-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="acc-btn acc-btn--primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountEditModal;
